import { Factory } from "@/data/dao/Factory";
import { PlanningDao } from "@/data/dao/service/PlanningDao";
import {
    ConsumptionSite,
    FulfillmentHandling,
    FulfillmentPositionDisplayMode,
    SchedulingMode,
} from "@/data/model/Enums";
import type { IOrderApplication } from "@/data/model/OrderApplication";
import { Format } from "@/data/util/Format";

import { translate } from "@/shared/localization/language";

import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { portalEvents } from "@/portal/events/portalEvents";

export class OrderApplicationTableController {
    private $element: JQuery;
    private dao: PlanningDao;
    private dataTable: DataTable<IOrderApplication>;
    private search: TableFilter;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<PlanningDao>(PlanningDao);

        const tableConfig: IDataTableConfig<IOrderApplication> = {
            columnMapping: [
                {
                    name: i18n("general.fields.designation"),
                    property: "name",
                    formatter: (m) => Format.htmlEscape(translate(m.name)),
                },
                {
                    name: i18n("useCase.fields.usePreorder"),
                    property: "usePreorder",
                    styles: { width: "10%" },
                    formatter: (m, p) => (p === true ? i18n("useCase.preorderOn") : i18n("useCase.preorderOff")),
                },
                {
                    name: i18n("useCase.fields.consumptionSite"),
                    property: "consumptionSite",
                    styles: { width: "10%" },
                    formatter: (m, p) => i18n("enums.ConsumptionSite." + ConsumptionSite[p]),
                },
                {
                    name: i18n("useCase.fields.fulfillmentPositionDisplayMode"),
                    property: "fulfillmentPositionDisplayMode",
                    styles: { width: "25%" },
                    formatter: (m, p) => {
                        const fulfillmentDisplayMode = FulfillmentPositionDisplayMode[m.fulfillmentPositionDisplayMode];
                        const displayFulfillment = i18n(
                            "enums.FulfillmentPositionDisplayMode." + fulfillmentDisplayMode
                        );

                        const displayFulfillmentHandling = i18n(
                            "enums.FulfillmentHandling." + FulfillmentHandling[m.fulfillmentHandling]
                        );
                        if (p == FulfillmentPositionDisplayMode.None) {
                            return displayFulfillment;
                        }

                        return `${displayFulfillment} - ${displayFulfillmentHandling}`;
                    },
                },
                {
                    name: i18n("useCase.fields.fulfillmentTimeMode"),
                    property: "schedulingMode",
                    styles: { width: "10%" },
                    formatter: (m, p) => i18n("enums.FulfillmentTimeMode." + SchedulingMode[p]),
                },
                {
                    name: i18n("useCase.fields.buyerRequirement"),
                    property: "ordererRequirements",
                    styles: { width: "12%" },
                    formatter: (m, p) => {
                        const ordererRequirements = m.ordererRequirements;
                        const requirements = new Array<string>();

                        if (ordererRequirements.isFirstNameRequired) {
                            requirements.push(i18n("general.fields.firstName"));
                        }

                        if (ordererRequirements.isLastNameRequired) {
                            requirements.push(i18n("general.fields.lastName"));
                        }

                        if (ordererRequirements.isEmailRequired) {
                            requirements.push(i18n("general.fields.email"));
                        }

                        if (ordererRequirements.isPhoneNumberRequired) {
                            requirements.push(i18n("general.fields.phone"));
                        }

                        if (ordererRequirements.isRegisteredRequired) {
                            requirements.push(i18n("allocationHistory.fields.isSignedUp"));
                        }

                        return requirements.join(", ");
                    },
                },
                {
                    name: i18n("useCase.fields.tip"),
                    property: "useTip",
                    styles: { width: "5%" },
                    formatter: (m, p) => (p ? i18n("general.yes") : i18n("general.no")),
                },
                {
                    name: "",
                    property: "id",
                    styles: { width: "110px", "text-align": "right" },
                    formatter: () => {
                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span>'
                        );
                    },
                },
            ],
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
            defaultSortAsc: true,
            locationDependend: true,
            defaultSortProperty: "name",
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.dataTable = new DataTable<IOrderApplication>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this.dao.useCaseDataSource,
            tableConfig
        );
        this.dataTable.onClick(
            (m) => {
                location.href = "/order-application/" + m.id;
            },
            (m) => {
                window.open("/order-application/" + m.id);
            }
        );

        this.dataTable.listenTo(this.search);
        portalEvents.OnLocationChanged.add(() => {
            this.dataTable.loadData(true);
        }, this);
    }
}
